import { css } from "@compiled/react";

import { HeaderTagTypes } from "@/components/wpl/content-block/src";
import { Layout } from "@/libraries/wpl/foundations/src";

import SimpleImageTextSide, {
    ImagePositions
} from "../../wpl/simple-image-text-side/src";

export interface PatternThreeAlternatingImageTextProps {
    backgroundColor?: string;
    backgroundImage?: string;
    horizontalRuleColor?: string;
    imageWidth?: string;
    rowHeaderType?: HeaderTagTypes;
    rowPropsOdd?: Layout;
    rowPropsEven?: Layout;

    imageTextSideRowOnePadding?: string;
    imageTextSideRowOnePaddingBelowLargeScreen?: string;

    rowOneHeader?: string | React.ReactNode;
    rowOneLozenge?: string;
    rowOneBody?: string | React.ReactNode;
    rowOneImageLink?: string;
    rowOneImageAltText?: string;
    rowOneImagePosition?: ImagePositions | undefined;
    rowOneEyebrowText?: string;

    imageTextSideRowTwoPadding?: string;
    imageTextSideRowTwoPaddingBelowLargeScreen?: string;
    rowTwoHeader?: string | React.ReactNode;
    rowTwoLozenge?: string;
    rowTwoBody?: string | React.ReactNode;
    rowTwoImageLink?: string;
    rowTwoImageAltText?: string;
    rowTwoImagePosition?: ImagePositions | undefined;
    rowTwoEyebrowText?: string;

    imageTextSideRowThreePadding?: string;
    imageTextSideRowThreePaddingBelowLargeScreen?: string;
    rowThreeHeader?: string | React.ReactNode;
    rowThreeLozenge?: string;
    rowThreeBody?: string | React.ReactNode;
    rowThreeImageLink?: string;
    rowThreeImageAltText?: string;
    rowThreeImagePosition?: ImagePositions | undefined;
    rowThreeEyebrowText?: string;

    imageTextSideRowFourPadding?: string;
    imageTextSideRowFourPaddingBelowLargeScreen?: string;
    rowFourHeader?: string | React.ReactNode;
    rowFourLozenge?: string;
    rowFourBody?: string | React.ReactNode;
    rowFourImageLink?: string;
    rowFourImageAltText?: string;
    rowFourImagePosition?: ImagePositions | undefined;
    rowFourEyebrowText?: string;
}

const ThreeAlternatingImageText = ({
    backgroundColor,
    backgroundImage,
    horizontalRuleColor,
    imageWidth,
    rowHeaderType = "h2",
    rowPropsOdd = "6-6",
    rowPropsEven = "6-6",
    imageTextSideRowOnePadding,
    imageTextSideRowOnePaddingBelowLargeScreen,
    rowOneHeader,
    rowOneLozenge,
    rowOneBody,
    rowOneImageLink,
    rowOneImageAltText,
    rowOneImagePosition = "left",
    rowOneEyebrowText,
    imageTextSideRowTwoPadding,
    imageTextSideRowTwoPaddingBelowLargeScreen,
    rowTwoHeader,
    rowTwoLozenge,
    rowTwoBody,
    rowTwoImageLink,
    rowTwoImageAltText,
    rowTwoImagePosition = "right",
    rowTwoEyebrowText,
    imageTextSideRowThreePadding,
    imageTextSideRowThreePaddingBelowLargeScreen,
    rowThreeHeader,
    rowThreeLozenge,
    rowThreeBody,
    rowThreeImageLink,
    rowThreeImageAltText,
    rowThreeImagePosition = "left",
    rowThreeEyebrowText,
    imageTextSideRowFourPadding,
    imageTextSideRowFourPaddingBelowLargeScreen,
    rowFourHeader,
    rowFourLozenge,
    rowFourBody,
    rowFourImageLink,
    rowFourImageAltText = "Alt Text",
    rowFourImagePosition,
    rowFourEyebrowText
}: PatternThreeAlternatingImageTextProps) => {
    const imageWidthValue = imageWidth ?? "550px";

    const imageTextSideRowOnePaddingValue =
        imageTextSideRowOnePadding ?? "64px 0 16px";
    const imageTextSideRowOnePaddingBelowLargeScreenValue =
        imageTextSideRowOnePaddingBelowLargeScreen ?? "64px 0 16px";

    const imageTextSideRowTwoPaddingValue =
        imageTextSideRowTwoPadding ?? "32px 0";
    const imageTextSideRowTwoPaddingBelowLargeScreenValue =
        imageTextSideRowTwoPaddingBelowLargeScreen ?? "16px 0";

    const imageTextSideRowThreePaddingValue =
        imageTextSideRowThreePadding ?? "16px 0 64px";
    const imageTextSideRowThreePaddingBelowLargeScreenValue =
        imageTextSideRowThreePaddingBelowLargeScreen ?? "16px 0 64px";

    const imageTextSideRowFourPaddingValue =
        imageTextSideRowFourPadding ?? "16px 0 64px";
    const imageTextSideRowFourPaddingBelowLargeScreenValue =
        imageTextSideRowFourPaddingBelowLargeScreen ?? "16px 0 64px";

    const ThreeAlternatingImageTextStyles = css({
        /* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
        // added to avoid breaking change since css is written to override styling far down the tree and passing in classnames to override styling in child components
        "img[class*=image-position]": {
            "width": imageWidthValue,
            "@media (max-width: 767.98px)": {
                order: 0,
                display: "block",
                marginInlineStart: "auto",
                marginInlineEnd: "auto"
            }
        },
        ".text-section": {
            "@media (min-width: 768px)": {
                maxWidth: "568px"
            },
            "h2": {
                "color": "#253858",
                "fontSize": "2.25rem",
                "margin": "0 0 24px ",
                "lineHeight": "1.33",
                "@media (max-width: 767.98px)": {
                    fontSize: "1.5rem",
                    margin: "0 0 14px "
                }
            },
            "hr": {
                margin: "0 0 40px",
                border: 0,
                backgroundColor: horizontalRuleColor
            },
            "p": {
                "color": "#091e42",
                "margin-bottom": 0
            }
        },
        ".image-text-side-row-1": {
            "padding": imageTextSideRowOnePaddingValue,
            "@media (max-width: 767.98px)": {
                padding: imageTextSideRowOnePaddingBelowLargeScreenValue
            }
        },
        ".image-text-side-row-2": {
            "padding": imageTextSideRowTwoPaddingValue,
            "@media (max-width: 767.98px)": {
                padding: imageTextSideRowTwoPaddingBelowLargeScreenValue
            }
        },
        ".image-text-side-row-3": {
            "padding": imageTextSideRowThreePaddingValue,
            "@media (max-width: 767.98px)": {
                padding: imageTextSideRowThreePaddingBelowLargeScreenValue
            }
        },
        ".image-text-side-row-4": {
            "padding": imageTextSideRowFourPaddingValue,
            "@media (max-width: 767.98px)": {
                padding: imageTextSideRowFourPaddingBelowLargeScreenValue
            }
        }
    });

    const isShowHorizontalRule = !!horizontalRuleColor;

    return (
        <section css={ThreeAlternatingImageTextStyles}>
            <div
                className="background-side-by-side"
                css={css({
                    backgroundColor: backgroundColor,
                    backgroundImage: backgroundImage
                })}
            >
                <SimpleImageTextSide
                    headerType={rowHeaderType}
                    headerText={rowOneHeader}
                    lozenge={rowOneLozenge}
                    bodyText={rowOneBody}
                    imageLink={rowOneImageLink}
                    imageAltText={rowOneImageAltText}
                    imagePosition={rowOneImagePosition}
                    horizontalRule={isShowHorizontalRule}
                    className="image-text-side-row-1"
                    eyebrowText={rowOneEyebrowText}
                    rowProps={rowPropsOdd}
                />
                <SimpleImageTextSide
                    headerType={rowHeaderType}
                    headerText={rowTwoHeader}
                    lozenge={rowTwoLozenge}
                    bodyText={rowTwoBody}
                    imageLink={rowTwoImageLink}
                    imageAltText={rowTwoImageAltText}
                    horizontalRule={isShowHorizontalRule}
                    imagePosition={rowTwoImagePosition}
                    className="image-text-side-row-2"
                    eyebrowText={rowTwoEyebrowText}
                    rowProps={rowPropsEven}
                />
                <SimpleImageTextSide
                    headerType={rowHeaderType}
                    headerText={rowThreeHeader}
                    lozenge={rowThreeLozenge}
                    bodyText={rowThreeBody}
                    imageLink={rowThreeImageLink}
                    imageAltText={rowThreeImageAltText}
                    imagePosition={rowThreeImagePosition}
                    horizontalRule={isShowHorizontalRule}
                    className="image-text-side-row-3"
                    eyebrowText={rowThreeEyebrowText}
                    rowProps={rowPropsOdd}
                />
                {rowFourImageLink ? (
                    <SimpleImageTextSide
                        headerType={rowHeaderType}
                        headerText={rowFourHeader}
                        lozenge={rowFourLozenge}
                        bodyText={rowFourBody}
                        imageLink={rowFourImageLink}
                        imageAltText={rowFourImageAltText}
                        imagePosition={rowFourImagePosition}
                        horizontalRule={isShowHorizontalRule}
                        className="image-text-side-row-4"
                        eyebrowText={rowFourEyebrowText}
                        rowProps={rowPropsEven}
                    />
                ) : null}
            </div>
        </section>
    );
};

ThreeAlternatingImageText.displayName = "ThreeAlternatingImageText";

export default ThreeAlternatingImageText;
